import { onAbort, UnanimousAbortController } from '@/utils/abort-util'

/* eslint-disable @typescript-eslint/no-explicit-any */

type KeySelector<F extends (...args: any) => any> = (
  args: Parameters<F>,
) => unknown

interface MemoizeOptionsBase<F extends (...args: any[]) => any> {
  /**
   * Used to derive the key.
   * If not specified, defaults to the first argument.
   *
   * @param args
   */
  keySelector?: KeySelector<F>

  /**
   * Max amount of items to cache.
   */
  maxCount?: number
}

type AbortSignalSelector<F extends (...args: any[]) => any> = (
  args: Parameters<F>,
) => AbortSignal | null | undefined

interface AsyncMemoizeOptions<
  F extends (...args: any[]) => any,
> extends MemoizeOptionsBase<F> {
  /**
   * When `true`, a rejection of the returned promise will clear the cache entry.
   * If a function, `true` is assumed, and the function will be used to
   * select the abort signal from the arguments.
   *
   * If the signal aborts while the operation is running,
   * the cache entry is removed immediately, even if the promise has
   * not yet resolved.
   *
   * When there are concurrent calls to the same entry, the abort signals will
   * be aggregated, and all of them must abort before the operation is aborted.
   */
  promise: true | AbortSignalSelector<F>
}

interface SyncMemoizeOptions<
  F extends (...args: any[]) => any,
> extends MemoizeOptionsBase<F> {
  promise?: false
}

type MemoizeOptions<F extends (...args: any[]) => any> =
  | AsyncMemoizeOptions<F>
  | SyncMemoizeOptions<F>

const NO_ARGS_CACHE_KEY = Symbol('no args cache key')
const CACHE = Symbol('memoize cache')

/**
 * Sync and async memoize implementation with support for abort signals.
 */
export function memoize<
  F extends (...args: any[]) => any,
  O extends MemoizeOptions<F>,
>(
  fn: O extends AsyncMemoizeOptions<F>
    ? F extends (...args: any[]) => PromiseLike<any>
      ? F
      : never
    : F extends (...args: any[]) => PromiseLike<any>
      ? never
      : F,
  options?: Readonly<O>,
): F {
  const keySelector = options?.keySelector ?? defaultKeySelector
  const maxCount = options?.maxCount ?? 0

  if (options?.promise) {
    const getAbortSignal: AbortSignalSelector<F> =
      typeof options.promise === 'function'
        ? options.promise
        : (args) => args.find((a) => a instanceof AbortSignal)

    return createAsyncMemoized(fn, keySelector, getAbortSignal, maxCount)
  }

  return createSyncMemoize(fn, keySelector, maxCount)

  /**
   * Creates a memoized function that is async.
   */
  function createAsyncMemoized(
    fn: F,
    keySelector: KeySelector<F>,
    getAbortSignal: AbortSignalSelector<F>,
    maxCount: number,
  ): F {
    const cache = new Map<unknown, CacheEntry<ReturnType<typeof fn>>>()

    function asyncMemoized(
      this: ThisType<F>,
      ...args: Parameters<F>
    ): PromiseLike<ReturnType<F>> {
      const key = keySelector(args)
      const signal = getAbortSignal(args)
      let entry = cache.get(key)

      // Tracks whether we've already cleared the cache for this run
      // due to either a failure or an abort.
      let cacheClearedForRun = false
      const tryRemoveKeyFromCache = () => {
        if (cacheClearedForRun) {
          return
        }
        cacheClearedForRun = true
        cache.delete(key)
      }

      if (entry) {
        if (signal) {
          entry.abortController?.add(signal)
        }

        return entry.promise
      }

      let controller: UnanimousAbortController | undefined = undefined
      let removeAbortListener: undefined | (() => unknown) = undefined
      if (signal) {
        controller = new UnanimousAbortController()
        controller.add(signal)

        // Replace the signal passed to the function with
        // the unanimous one. This assumes it's passed directly as an
        // argument and not in any sort of wrapper object.
        for (let i = 0; i < args.length; i++) {
          if (args[i] === signal) {
            args[i] = controller.signal
          }
        }
      }

      entry = {
        promise: fn.apply(this, args) as ReturnType<F>,
        abortController: controller,
      }

      cache.set(key, entry)

      // Remove the cache entry if the signal aborts before we finish running.
      removeAbortListener = onAbort(controller?.signal, tryRemoveKeyFromCache)

      maybeCleanupCache(cache, maxCount)
      entry.promise.then(
        () => {
          entry.abortController?.dispose()
          removeAbortListener?.()
        },
        () => {
          removeAbortListener?.()
          tryRemoveKeyFromCache()
        },
      )

      return entry.promise
    }

    asyncMemoized[CACHE] = cache
    return asyncMemoized as F & { [CACHE]: typeof cache }
  }

  /**
   * Creates a memoized function that is synchronous.
   */
  function createSyncMemoize(
    fn: F,
    keySelector: KeySelector<F>,
    maxCount: number,
  ): F {
    const cache = new Map<unknown, ReturnType<F>>()
    function syncMemoized(
      this: ThisType<F>,
      ...args: Parameters<F>
    ): ReturnType<F> {
      const key = keySelector(args)

      if (cache.has(key)) {
        return cache.get(key)! as ReturnType<F>
      }

      const result = fn.call(this, ...args) as ReturnType<F>
      cache.set(key, result)

      maybeCleanupCache(cache, maxCount)

      return result
    }

    syncMemoized[CACHE] = cache
    return syncMemoized as F & { [CACHE]: typeof cache }
  }

  /**
   * Cleans up the cache if we've overrun capacity.
   */
  function maybeCleanupCache(cache: Map<unknown, unknown>, maxCount: number) {
    if (maxCount > 0 && cache.size > maxCount) {
      // We've exceeded the max size; remove the first inserted item.
      // noinspection LoopStatementThatDoesntLoopJS
      for (const key of cache.keys()) {
        cache.delete(key)
        break
      }
    }
  }

  /**
   * Default key selector.
   * @param args
   */
  function defaultKeySelector(args: Parameters<F>): unknown {
    if (args.length === 0) {
      return NO_ARGS_CACHE_KEY
    }

    // Special case where the function is called with a single argument, and it's
    // an abort signal.
    if (args.length === 1 && args[0] instanceof AbortSignal) {
      return NO_ARGS_CACHE_KEY
    }

    // Use the first argument as the cache key.
    return `${args[0]}`
  }
}

interface CacheEntry<T> {
  promise: PromiseLike<T>
  abortController?: UnanimousAbortController
}

/**
 * Clears the given memoized function's cache if it has one.
 *
 * @param memoized The memoized function to clear the cache for.
 * @param key If specified, clears just this cache key entry.
 */
export function clearMemoized<F extends (...args: any[]) => any>(
  memoized: F,
  key?: string,
) {
  if (CACHE in memoized) {
    const cache = memoized[CACHE] as Map<string, unknown>
    if (typeof key === 'string') {
      cache.delete(key)
    } else {
      cache.clear()
    }
  }
}
